import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { encodeAbiParameters, keccak256 } from 'viem';
import { signMessage } from 'viem/accounts';
import { z } from 'zod';

import { getTransfers } from '@/features/account/api/get-transfers';
import { usePrivyAccount } from '@/hooks';
import { useAccountStore } from '@/store/use-account-store';
import { COLLATERAL_DECIMALS } from '@/store/use-markets-store';
import { useTransfersStore } from '@/store/use-transfers-store';
import { EventType } from '@/types';
import { Entity } from '@/types/api';
import { ErrorCode, ErrorMsgs, OrderStatus } from '@/types/enums';
import { sendEvent } from '@/utils/analytics';
import {
  handleTransferForStore,
  transferResponseToTransfer,
} from '@/utils/handle-store-data';
import { bigIntToNum } from '@/utils/value-format';

import { privateApi } from '../api-clients/rest-client';

const createWithdrawInputSchema = z.object({
  size: z.bigint(),
});

type CreateWithdrawParams = z.infer<typeof createWithdrawInputSchema>;

type CreateWithdrawResponse = Entity<{
  id: string;
  status: string; // NEW
}>;

export default function useCreateFundedWithdraw() {
  const { email, address: recipient } = usePrivyAccount();

  return useMutation({
    mutationFn: async ({
      size,
    }: CreateWithdrawParams): Promise<CreateWithdrawResponse> => {
      if (!recipient)
        throw new Error(
          'No primary recipient account found, please contact support.',
        );

      const {
        address = '',
        getAuthParams,
        nextNonce,
        getAccurateTime,
      } = useAccountStore.getState();
      const authParams = getAuthParams();

      if (!authParams?.signingKey) throw new Error('No signing key found.');
      if (!authParams.address)
        throw new Error('No sender address found, please contact support.');

      const time = getAccurateTime();
      const nonce = nextNonce();

      const encodedOrder = encodeAbiParameters(
        [
          { name: 'time', type: 'uint256' },
          { name: 'nonce', type: 'uint256' },
          { name: 'account', type: 'address' },
          { name: 'recipient', type: 'address' },
          { name: 'size', type: 'uint256' },
        ],
        [
          BigInt(time),
          BigInt(nonce),
          authParams.address as `0x${string}`,
          recipient as `0x${string}`,
          size,
        ],
      );

      const signature = await signMessage({
        message: { raw: keccak256(encodedOrder) },
        privateKey: authParams.signingKey,
      });

      const body = {
        order: {
          size: Number(size),
          nonce,
          time,
          recipient,
        },
        recvWindow: 0,
        signature,
      };

      const promise = privateApi.post('/transfer/withdraw/profits', body);

      const toastPromise = promise.then((res) => {
        const { id: orderId } = res;
        sendEvent(EventType.WITHDRAW_INITIATED, {
          amount: bigIntToNum(size, COLLATERAL_DECIMALS),
          email,
        });
        return new Promise((resolve, reject) => {
          // eslint-disable-next-line prefer-const
          let subscriberTimeoutId: NodeJS.Timeout;
          // eslint-disable-next-line prefer-const
          let pollIntervalId: NodeJS.Timeout;
          // eslint-disable-next-line prefer-const
          let unsubscribe: () => void;

          const cleanup = () => {
            clearTimeout(subscriberTimeoutId);
            clearInterval(pollIntervalId);
            unsubscribe();
          };

          unsubscribe = useTransfersStore.subscribe((state) => {
            const transfers = state.getTransfers(address);
            if (orderId in transfers) {
              cleanup();
              const { status } = transfers[orderId];
              if (status === OrderStatus.NEW || status === OrderStatus.FILLED) {
                resolve(transfers[orderId]);
              } else if (status === OrderStatus.REJECTED) {
                reject(transfers[orderId].code as ErrorCode);
              }
            }
          });

          subscriberTimeoutId = setTimeout(() => {
            cleanup();
            reject(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT);
          }, 30_000); // 30 seconds timeout

          pollIntervalId = setInterval(() => {
            getTransfers({ id: orderId }).then((res) =>
              res.forEach((data) =>
                handleTransferForStore(transferResponseToTransfer(data)),
              ),
            );
          }, 2_000);
        });
      });

      toast.promise(toastPromise, {
        loading: 'Requesting withdrawal...',
        success: (
          <span>
            Successfully requested withdrawal: see{' '}
            <button
              className="underline"
              onClick={() => {
                const navigate = new CustomEvent('app:navigate', {
                  detail: { pathname: '/portfolio', search: '?view=transfers' },
                });
                window.dispatchEvent(navigate);
              }}
            >
              Transfers
            </button>
          </span>
        ),
        error: (error) => `Withdraw failed: ${ErrorMsgs[error as ErrorCode]}`,
      });

      return promise;
    },
  });
}
